import {CSSProperties, useId, useState} from "react";
import classNames from "classnames";

import {Button, IButtonSizePrimary} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";

import {HOMEMATCH_TINDER_PICKS_CARD_CONTENT} from "../constants/homematch_tinder_picks_card_content";
import {IHomematchTinderPicksTeaser} from "../types/IHomematchTinderPicksTeaser";

import * as styles from "./HomematchTinderPicksCard.module.css";

interface IProps {
    ctaDataTestId?: string;
    ctaLabel?: string;
    ctaSize?: IButtonSizePrimary;
    ctaVariant?: "filled_primary" | "filled_secondary" | "outlined_secondary";
    dataTestId?: string;
    layout?: "responsive" | "stacked";
    onOpenRecommendations?: () => void;
    onRecommendationsClick?: (ctaLabel: string) => void;
    rowGap?: string;
    subheading?: string;
    teaser: IHomematchTinderPicksTeaser;
}

interface IPhotoStackProps {
    badgeLabel: string;
    imageUrls: string[];
}

const EMPTY_STATE_ILLUSTRATION = require("../assets/empty_picks_binoculars.svg");

const PHOTO_STACK_LAYOUT = [styles.photoStackBack, styles.photoStackMiddle, styles.photoStackFront];

const HomematchTinderPicksPhotoStack = (props: IPhotoStackProps) => {
    const {badgeLabel, imageUrls} = props;

    const [settledCount, setSettledCount] = useState(0);
    const lastPreviewIndex = imageUrls.length - 1;
    const areImagesReady = imageUrls.length === 0 || settledCount >= imageUrls.length;

    const handleImageSettled = () => setSettledCount((count) => count + 1);

    return (
        <div className={styles.photoStack}>
            {!areImagesReady && (
                <div className={styles.photoStackLoader}>
                    <Loader size="lg" />
                </div>
            )}

            {imageUrls.map((imageUrl, index) => (
                <div className={classNames(PHOTO_STACK_LAYOUT[index], !areImagesReady && styles.photoStackPending)} key={imageUrl}>
                    <img alt="" className={styles.photoStackImage} onError={handleImageSettled} onLoad={handleImageSettled} src={imageUrl} />

                    {index === lastPreviewIndex && (
                        <Text as="span" className={styles.photoStackBadge} variant="info_txt_3">
                            {badgeLabel}
                        </Text>
                    )}
                </div>
            ))}
        </div>
    );
};

export const HomematchTinderPicksCard = (props: IProps) => {
    const {
        ctaDataTestId,
        ctaLabel,
        ctaSize = "medium",
        ctaVariant = "filled_secondary",
        dataTestId,
        layout = "responsive",
        onOpenRecommendations,
        onRecommendationsClick,
        rowGap,
        subheading,
        teaser
    } = props;

    const headingId = useId();

    const isStacked = layout === "stacked";
    const previewImageUrls = teaser.imageUrls.slice(0, PHOTO_STACK_LAYOUT.length);
    // New image set remounts the stack, so the spinner restarts for the new photos.
    const photoStackKey = previewImageUrls.join("|");
    const resolvedCtaLabel = ctaLabel ?? HOMEMATCH_TINDER_PICKS_CARD_CONTENT.getCtaLabel(teaser.offersCount);
    const cardStyle = rowGap ? ({"--picks-card-row-gap": rowGap} as CSSProperties) : undefined;

    const handleRecommendationsActivate = () => {
        onRecommendationsClick?.(resolvedCtaLabel);
        onOpenRecommendations?.();
    };

    if (teaser.source !== "api") {
        return (
            <section aria-labelledby={headingId} className={classNames(styles.emptyCard, isStacked && styles.stacked)} data-testid={dataTestId}>
                <header className={styles.emptyHead}>
                    <Text as="h2" className={styles.heading} id={headingId} variant="headline_6">
                        {HOMEMATCH_TINDER_PICKS_CARD_CONTENT.EMPTY_TITLE}
                    </Text>

                    <Text as="p" className={styles.subheading} variant="info_txt_2">
                        {HOMEMATCH_TINDER_PICKS_CARD_CONTENT.EMPTY_DESCRIPTION}
                    </Text>
                </header>

                <div className={styles.emptyIllustrationArea}>
                    <img alt="" className={styles.emptyIllustration} loading="lazy" src={EMPTY_STATE_ILLUSTRATION} />
                </div>
            </section>
        );
    }

    return (
        <section aria-labelledby={headingId} className={classNames(styles.card, isStacked && styles.stacked)} data-testid={dataTestId} style={cardStyle}>
            <header className={styles.head}>
                <Text as="h2" className={styles.heading} id={headingId} variant="headline_6">
                    {HOMEMATCH_TINDER_PICKS_CARD_CONTENT.TITLE}
                </Text>

                <Text as="p" className={styles.subheading} variant="info_txt_2">
                    {subheading ?? HOMEMATCH_TINDER_PICKS_CARD_CONTENT.SUBHEADING}
                </Text>
            </header>

            {onOpenRecommendations ? (
                <button aria-label={resolvedCtaLabel} className={styles.photoStackArea} onClick={handleRecommendationsActivate} type="button">
                    <HomematchTinderPicksPhotoStack badgeLabel={teaser.badgeLabel} imageUrls={previewImageUrls} key={photoStackKey} />
                </button>
            ) : (
                <a aria-label={resolvedCtaLabel} className={styles.photoStackArea} href={teaser.href} onClick={handleRecommendationsActivate}>
                    <HomematchTinderPicksPhotoStack badgeLabel={teaser.badgeLabel} imageUrls={previewImageUrls} key={photoStackKey} />
                </a>
            )}

            <Button
                className={styles.button}
                dataTestId={ctaDataTestId}
                href={onOpenRecommendations ? undefined : teaser.href}
                onClick={handleRecommendationsActivate}
                size={ctaSize}
                variant={ctaVariant}
            >
                {resolvedCtaLabel}
            </Button>
        </section>
    );
};
import {CSSProperties, useId, useState} from "react";
import classNames from "classnames";

import {Button, IButtonSizePrimary} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";

import {HOMEMATCH_TINDER_PICKS_CARD_CONTENT} from "../constants/homematch_tinder_picks_card_content";
import {IHomematchTinderPicksTeaser} from "../types/IHomematchTinderPicksTeaser";

import * as styles from "./HomematchTinderPicksCard.module.css";

interface IProps {
    ctaDataTestId?: string;
    ctaLabel?: string;
    ctaSize?: IButtonSizePrimary;
    ctaVariant?: "filled_primary" | "filled_secondary" | "outlined_secondary";
    dataTestId?: string;
    layout?: "responsive" | "stacked";
    onOpenRecommendations?: () => void;
    onRecommendationsClick?: (ctaLabel: string) => void;
    rowGap?: string;
    subheading?: string;
    teaser: IHomematchTinderPicksTeaser;
}

interface IPhotoStackProps {
    badgeLabel: string;
    imageUrls: string[];
}

const EMPTY_STATE_ILLUSTRATION = require("../assets/empty_picks_binoculars.svg");

const PHOTO_STACK_LAYOUT = [styles.photoStackBack, styles.photoStackMiddle, styles.photoStackFront];

const HomematchTinderPicksPhotoStack = (props: IPhotoStackProps) => {
    const {badgeLabel, imageUrls} = props;

    const [settledCount, setSettledCount] = useState(0);
    const lastPreviewIndex = imageUrls.length - 1;
    const areImagesReady = imageUrls.length === 0 || settledCount >= imageUrls.length;

    const handleImageSettled = () => setSettledCount((count) => count + 1);

    return (
        <div className={styles.photoStack}>
            {!areImagesReady && (
                <div className={styles.photoStackLoader}>
                    <Loader size="lg" />
                </div>
            )}

            {imageUrls.map((imageUrl, index) => (
                <div className={classNames(PHOTO_STACK_LAYOUT[index], !areImagesReady && styles.photoStackPending)} key={imageUrl}>
                    <img alt="" className={styles.photoStackImage} onError={handleImageSettled} onLoad={handleImageSettled} src={imageUrl} />

                    {index === lastPreviewIndex && (
                        <Text as="span" className={styles.photoStackBadge} variant="info_txt_3">
                            {badgeLabel}
                        </Text>
                    )}
                </div>
            ))}
        </div>
    );
};

export const HomematchTinderPicksCard = (props: IProps) => {
    const {
        ctaDataTestId,
        ctaLabel,
        ctaSize = "medium",
        ctaVariant = "filled_secondary",
        dataTestId,
        layout = "responsive",
        onOpenRecommendations,
        onRecommendationsClick,
        rowGap,
        subheading,
        teaser
    } = props;

    const headingId = useId();

    const isStacked = layout === "stacked";
    const previewImageUrls = teaser.imageUrls.slice(0, PHOTO_STACK_LAYOUT.length);
    // New image set remounts the stack, so the spinner restarts for the new photos.
    const photoStackKey = previewImageUrls.join("|");
    const resolvedCtaLabel = ctaLabel ?? HOMEMATCH_TINDER_PICKS_CARD_CONTENT.getCtaLabel(teaser.offersCount);
    const cardStyle = rowGap ? ({"--picks-card-row-gap": rowGap} as CSSProperties) : undefined;

    const handleRecommendationsActivate = () => {
        onRecommendationsClick?.(resolvedCtaLabel);
        onOpenRecommendations?.();
    };

    if (teaser.source !== "api") {
        return (
            <section aria-labelledby={headingId} className={classNames(styles.emptyCard, isStacked && styles.stacked)} data-testid={dataTestId}>
                <header className={styles.emptyHead}>
                    <Text as="h2" className={styles.heading} id={headingId} variant="headline_6">
                        {HOMEMATCH_TINDER_PICKS_CARD_CONTENT.EMPTY_TITLE}
                    </Text>

                    <Text as="p" className={styles.subheading} variant="info_txt_2">
                        {HOMEMATCH_TINDER_PICKS_CARD_CONTENT.EMPTY_DESCRIPTION}
                    </Text>
                </header>

                <div className={styles.emptyIllustrationArea}>
                    <img alt="" className={styles.emptyIllustration} loading="lazy" src={EMPTY_STATE_ILLUSTRATION} />
                </div>
            </section>
        );
    }

    return (
        <section aria-labelledby={headingId} className={classNames(styles.card, isStacked && styles.stacked)} data-testid={dataTestId} style={cardStyle}>
            <header className={styles.head}>
                <Text as="h2" className={styles.heading} id={headingId} variant="headline_6">
                    {HOMEMATCH_TINDER_PICKS_CARD_CONTENT.TITLE}
                </Text>

                <Text as="p" className={styles.subheading} variant="info_txt_2">
                    {subheading ?? HOMEMATCH_TINDER_PICKS_CARD_CONTENT.SUBHEADING}
                </Text>
            </header>

            {onOpenRecommendations ? (
                <button aria-label={resolvedCtaLabel} className={styles.photoStackArea} onClick={handleRecommendationsActivate} type="button">
                    <HomematchTinderPicksPhotoStack badgeLabel={teaser.badgeLabel} imageUrls={previewImageUrls} key={photoStackKey} />
                </button>
            ) : (
                <a aria-label={resolvedCtaLabel} className={styles.photoStackArea} href={teaser.href} onClick={handleRecommendationsActivate}>
                    <HomematchTinderPicksPhotoStack badgeLabel={teaser.badgeLabel} imageUrls={previewImageUrls} key={photoStackKey} />
                </a>
            )}

            <Button
                className={styles.button}
                dataTestId={ctaDataTestId}
                href={onOpenRecommendations ? undefined : teaser.href}
                onClick={handleRecommendationsActivate}
                size={ctaSize}
                variant={ctaVariant}
            >
                {resolvedCtaLabel}
            </Button>
        </section>
    );
};
